import React, { memo, useCallback, useRef, useEffect } from 'react';
import { FixedSizeList as List } from 'react-window';
import MessageBubble from './MessageBubble';
import { useDarkMode } from '../contexts/DarkModeContext';

// ⚡️ PERFORMANCE OPTIMIZATION: Virtualized message list for large chat histories
const VirtualizedMessageList = memo(({
  messages,
  user,
  isAdminDashboard,
  conversation,
  onImageClick,
  renderMessageStatus,
  containerHeight = 600,
  itemHeight = 80
}) => {
  const { isDarkMode } = useDarkMode();
  const listRef = useRef(null);
  const lastMessageRef = useRef(null);

  // ⚡️ PERFORMANCE OPTIMIZATION: Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messages.length > 0 && listRef.current) {
      const lastIndex = messages.length - 1;
      listRef.current.scrollToItem(lastIndex, 'end');
    }
  }, [messages.length]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Memoized row renderer
  const Row = useCallback(({ index, style }) => {
    const message = messages[index];
    if (!message) return null;

    return (
      <div style={style}>
        <MessageBubble
          msg={message}
          user={user}
          isAdminDashboard={isAdminDashboard}
          conversation={conversation}
          onImageClick={onImageClick}
          renderMessageStatus={renderMessageStatus}
        />
      </div>
    );
  }, [messages, user, isAdminDashboard, conversation, onImageClick, renderMessageStatus]);

  // ⚡️ PERFORMANCE OPTIMIZATION: Dynamic item height calculation
  const getItemHeight = useCallback((index) => {
    const message = messages[index];
    if (!message) return itemHeight;

    // Adjust height based on message type and content
    if (message.type === 'image' || message.type === 'video') {
      return 200; // Media messages need more space
    }
    
    if (message.text && message.text.length > 100) {
      return Math.max(itemHeight, 60 + Math.floor(message.text.length / 50) * 20);
    }

    return itemHeight;
  }, [messages, itemHeight]);

  if (!messages || messages.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full ${
        isDarkMode ? 'text-gray-400' : 'text-gray-500'
      }`}>
        No messages yet
      </div>
    );
  }

  return (
    <div className="virtualized-message-list">
      <List
        ref={listRef}
        height={containerHeight}
        itemCount={messages.length}
        itemSize={getItemHeight}
        width="100%"
        overscanCount={5} // Render 5 items above/below viewport for smooth scrolling
        className="custom-scrollbar"
      >
        {Row}
      </List>
    </div>
  );
});

VirtualizedMessageList.displayName = 'VirtualizedMessageList';

export default VirtualizedMessageList; 